/**
 * ParentChildSystem - Core utility for managing parent-child relationships
 * between objects using a consistent parents array structure.
 *
 * This system replaces the inconsistent parent fields (parentIdentifier,
 * parentWallId, etc.) with a unified parents array containing id and type.
 */

import {
  isSink,
  SINK_TYPE,
  COUNTERTOP_MOUNTED_SINK_TYPES,
} from '@shared-lib/sink-types';
import {
  SHOWER_WALL_PART_TYPES,
  followShowerWall,
} from '@shared-lib/geometry/showerWallParts';

class ParentChildSystem {
  /**
   * Get the primary parent (first in parents array)
   * @param {Object} object - Object to get parent from
   * @returns {Object|null} Parent reference {id, type} or null
   */
  static getPrimaryParent(object) {
    return object?.parents?.[0] || null;
  }

  /**
   * Get parent of specific type
   * @param {Object} object - Object to search parents
   * @param {string} parentType - Type of parent to find
   * @returns {Object|null} Parent reference {id, type} or null
   */
  static getParentByType(object, parentType) {
    if (!object?.parents || !Array.isArray(object.parents)) {
      return null;
    }
    return object.parents.find((p) => p.type === parentType) || null;
  }

  /**
   * Find all children of a parent
   * @param {string} parentId - ID of the parent object
   * @param {Object} room - Room containing objects
   * @param {string|null} childType - Optional filter by child type
   * @param {boolean} primaryOnly - If true, only return objects where this is the primary (first) parent
   * @returns {Array} Array of child objects
   */
  static getChildren(parentId, room, childType = null, primaryOnly = false) {
    if (!room?.objects || !Array.isArray(room.objects)) {
      return [];
    }

    const children = room.objects.filter((obj) => {
      // Check if object has this parent in parents array
      let hasParent;
      if (primaryOnly) {
        // Only match if this is the primary (first) parent
        const primaryParent = obj.parents?.[0];
        hasParent = primaryParent && primaryParent.id === parentId;
      } else {
        // Match if parent appears anywhere in parents array
        hasParent = obj.parents?.some((p) => p.id === parentId);
      }

      // Check type if specified
      const matchesType = !childType || obj.type === childType;

      return hasParent && matchesType;
    });

    return children;
  }

  /**
   * Add parent relationship to an object
   * @param {Object} object - Object to add parent to
   * @param {string} parentId - ID of the parent
   * @param {string} parentType - Type of the parent
   * @returns {boolean} True if parent was added, false if already exists
   */
  static addParent(object, parentId, parentType) {
    if (!object) {
      return false;
    }

    // Initialize parents array if it doesn't exist
    if (!object.parents) {
      object.parents = [];
    }

    // Check if parent already exists
    const existingParent = object.parents.find(
      (p) => p.id === parentId && p.type === parentType
    );

    if (existingParent) {
      return false; // Parent already exists
    }

    // Add the parent
    object.parents.push({ id: parentId, type: parentType });
    return true;
  }

  /**
   * Remove parent relationship from an object
   * @param {Object} object - Object to remove parent from
   * @param {string} parentId - ID of the parent to remove
   * @returns {boolean} True if parent was removed, false if not found
   */
  static removeParent(object, parentId) {
    if (!object?.parents || !Array.isArray(object.parents)) {
      return false;
    }

    const initialLength = object.parents.length;
    object.parents = object.parents.filter((p) => p.id !== parentId);

    return object.parents.length < initialLength;
  }

  /**
   * Validate if a parent-child relationship is allowed.
   * @param {Object} child - Child object (must have at minimum a `type` field)
   * @param {Object} parent - Parent object (must have at minimum a `type` field)
   * @returns {boolean} True if relationship is valid
   */
  static validateRelationship(child, parent) {
    const childType = child?.type;
    const parentType = parent?.type;

    // Sinks are handled with sinkType-aware logic rather than a static map entry.
    // Only countertop-mounted sink types (undermount, dropin, vessel) may have a
    // countertop parent. Standalone types (pedestal, wall-mount) have no allowed
    // parent. A sink without a sinkType field is conservatively rejected — we
    // cannot confirm it is countertop-mounted without explicit mount data. We
    // read child.sinkType directly rather than getSinkType() to avoid the legacy
    // bare-sink fallback (getSinkType treats bare {type:'sink'} as undermount for
    // backward compat during migration; here we only accept post-migration objects).
    if (childType === SINK_TYPE) {
      const mountType = child.sinkType;
      if (!mountType) {
        return false;
      }
      return (
        COUNTERTOP_MOUNTED_SINK_TYPES.includes(mountType) &&
        parentType === 'countertop'
      );
    }
    // Kitchen sinks (top-level type 'kitchen-sink') always mount to a
    // countertop. Their subtype (undermount / drop-in / farmhouse) is
    // not load-bearing for parent validation — every kitchen-sink
    // subtype is countertop-mounted.
    if (childType === 'kitchen-sink') {
      return parentType === 'countertop' || parentType === 'lower-cabinet';
    }

    // Define valid parent-child relationships for all other object types
    const validRelationships = {
      // Countertops can be parented to cabinets
      countertop: ['lower-cabinet', 'upper-cabinet'],

      // Faucets can be parented to sinks (canonical type only)
      'bathroom-sink-faucet': ['sink'],
      'kitchen-faucet': ['kitchen-sink', 'countertop'],

      // Shower components can be parented to showers
      showerhead: ['shower'],
      'shower-controls': ['shower'],
      'shower-bench': ['shower'],
      'shower-niche': ['shower'],
      // its accessories (step 3a); a tub/shower's hang off the tub
      'shower-shelf': ['shower', 'bathtub'],
      'corner-caddy': ['shower', 'bathtub'],
      'shower-seat': ['shower'],
      'grab-bar': ['shower', 'bathtub'],
      'straight-sliding-door-shower': ['shower'],

      // Wall-mounted elements
      door: ['wall'],
      window: ['wall'],
      opening: ['wall'],
      'wall-light': ['wall'],
      mirror: ['wall'],

      // Default: if not defined, allow the relationship
      // This provides flexibility for new object types
    };

    const validParents = validRelationships[childType];

    // If no restrictions defined, allow the relationship
    if (!validParents) {
      return true;
    }

    return validParents.includes(parentType);
  }

  /**
   * Build a map of parent-child relationships for efficient lookups
   * @param {Object} room - Room containing objects
   * @returns {Object} Map of parentId -> [children]
   */
  static buildChildrenMap(room) {
    const map = {};

    if (!room?.objects || !Array.isArray(room.objects)) {
      return map;
    }

    room.objects.forEach((obj) => {
      if (obj.parents && Array.isArray(obj.parents)) {
        obj.parents.forEach((parent) => {
          if (!map[parent.id]) {
            map[parent.id] = [];
          }
          map[parent.id].push(obj);
        });
      }
    });

    return map;
  }

  /**
   * Cascade movement to all children (enhanced version)
   * Only cascades to PRIMARY children (where this object is the first parent in the parents array)
   * This prevents double-movement when objects have multiple parents
   * @param {string} parentId - ID of moved parent object
   * @param {Object} offset - Movement offset {x, y, z}
   * @param {Object} room - Current room data
   * @param {Object} options - Configuration options
   * @param {boolean} options.recursive - Cascade to descendants recursively (default: true)
   * @param {boolean} options.excludeWalls - Skip walls and their children (default: false)
   * @param {number} options.maxDepth - Max recursion depth (default: 10)
   * @param {string[]} options.excludeTypes - Object types to skip
   * @returns {Object} Updated room object with cascaded changes (new object, not mutated)
   */
  static cascadeMovement(parentId, offset, room, options = {}) {
    // Handle legacy signature for backward compatibility
    if (
      typeof parentId === 'object' &&
      parentId !== null &&
      !Array.isArray(parentId)
    ) {
      // Old signature: cascadeMovement(movedObject, delta, room)
      const movedObject = parentId;
      const delta = offset;
      parentId = movedObject.id;
      offset = delta;
      options = {};
    }

    // Set default options
    const defaults = {
      recursive: true,
      excludeWalls: false,
      maxDepth: 10,
      excludeTypes: [],
    };
    const config = { ...defaults, ...options };

    // Validate inputs
    if (!parentId || typeof parentId !== 'string') {
      console.warn('cascadeMovement: Invalid parentId');
      return room;
    }

    if (
      !offset ||
      typeof offset !== 'object' ||
      typeof offset.x !== 'number' ||
      typeof offset.y !== 'number' ||
      typeof offset.z !== 'number'
    ) {
      console.warn('cascadeMovement: Invalid offset object');
      return room;
    }

    if (!room || !room.objects) {
      console.warn('cascadeMovement: Invalid room object');
      return room;
    }

    // Clone room for immutable update
    const updatedRoom = JSON.parse(JSON.stringify(room));

    // Track visited objects to prevent infinite loops
    const visited = new Set();

    // Recursive function to update children
    const updateChildren = (currentParentId, currentDepth = 0) => {
      // Safety checks
      if (currentDepth >= config.maxDepth) {
        console.warn(`cascadeMovement: Max depth ${config.maxDepth} exceeded`);
        return;
      }

      if (visited.has(currentParentId)) {
        console.error('cascadeMovement: Circular dependency detected');
        return;
      }

      visited.add(currentParentId);

      // Get only PRIMARY children (where this is the first parent in their parents array)
      // Note: For childObjectId relationships, we don't use primaryOnly restriction
      const children = this.getChildren(
        currentParentId,
        updatedRoom,
        null,
        true
      );

      // At root level, also include fixture group members (e.g., showerhead,
      // shower-controls, shower-niche that share the same fixtureGroupId)
      if (currentDepth === 0) {
        const groupMembers = this.getFixtureGroupMembers(
          currentParentId,
          updatedRoom
        );
        groupMembers.forEach((member) => {
          if (!children.some((c) => c.id === member.id)) {
            children.push(member);
          }
        });
      }

      children.forEach((child) => {
        // Skip excluded types
        if (config.excludeTypes.includes(child.type)) {
          return;
        }

        // Skip walls and their children if excludeWalls is true
        if (config.excludeWalls && child.type === 'wall') {
          return;
        }

        const childId = child.id;

        // Update child position
        if (child.center) {
          child.center.x += offset.x;
          child.center.y += offset.y;
          child.center.z += offset.z;
        }

        // Also update position field if it exists (for backward compatibility)
        if (child.position) {
          child.position.x += offset.x;
          child.position.y += offset.y;
          child.position.z += offset.z;
        }

        // Recursively update grandchildren if enabled
        if (config.recursive) {
          if (childId) {
            updateChildren(childId, currentDepth + 1);
          }
        }
      });
    };

    updateChildren(parentId, 0);

    return updatedRoom;
  }

  /**
   * Cascade deletion - delete object and all its descendants
   * @param {Object} objectToDelete - Object to delete
   * @param {Object} room - Room containing objects
   * @returns {number} Number of objects deleted
   */
  static cascadeDelete(objectToDelete, room) {
    if (!objectToDelete || !room?.objects) {
      return 0;
    }

    const objectId = objectToDelete.id;
    if (!objectId) {
      return 0;
    }

    // Find all descendants
    const toDelete = new Set([objectId]);
    const addDescendants = (parentId) => {
      const children = this.getChildren(parentId, room);
      children.forEach((child) => {
        const childId = child.id;
        if (childId && !toDelete.has(childId)) {
          toDelete.add(childId);
          addDescendants(childId);
        }
      });
    };

    addDescendants(objectId);

    // Remove all objects in the deletion set
    const initialCount = room.objects.length;
    room.objects = room.objects.filter((obj) => {
      const objId = obj.id;
      return !toDelete.has(objId);
    });

    return initialCount - room.objects.length;
  }

  /**
   * Check if an object has a specific parent
   * @param {Object} object - Object to check
   * @param {string} parentId - Parent ID to look for
   * @returns {boolean} True if object has this parent
   */
  static hasParent(object, parentId) {
    if (!object?.parents || !Array.isArray(object.parents)) {
      return false;
    }
    return object.parents.some((p) => p.id === parentId);
  }

  /**
   * Check if an object has a parent of specific type
   * @param {Object} object - Object to check
   * @param {string} parentType - Parent type to look for
   * @returns {boolean} True if object has a parent of this type
   */
  static hasParentType(object, parentType) {
    if (!object?.parents || !Array.isArray(object.parents)) {
      return false;
    }
    return object.parents.some((p) => p.type === parentType);
  }

  /**
   * Get the full hierarchy chain for an object
   * @param {string} objectId - ID of the object
   * @param {Object} room - Room containing objects
   * @returns {Array} Array of {id, type} objects from child to root
   */
  static getHierarchyChain(objectId, room) {
    const chain = [];
    const visited = new Set(); // Prevent infinite loops

    let current = room.objects?.find((o) => o.id === objectId);

    while (current && !visited.has(current.id)) {
      const currentId = current.id;
      visited.add(currentId);

      chain.push({
        id: currentId,
        type: current.type,
      });

      // Get primary parent
      const parent = this.getPrimaryParent(current);
      if (!parent) break;

      // Find parent object
      current = room.objects?.find((o) => o.id === parent.id);
    }

    return chain;
  }

  /**
   * Find all descendants of a parent object
   * @param {string} parentId - ID of the parent object
   * @param {Object} room - Room containing objects
   * @param {boolean} includeDepth - If true, include depth level in results
   * @returns {Array} Array of descendant objects (optionally with depth)
   */
  static findAllDescendants(parentId, room, includeDepth = false) {
    const descendants = [];
    const visited = new Set();
    const addedDescendants = new Set(); // Track added descendants to prevent duplicates

    const findDescendantsRecursive = (currentParentId, depth = 1) => {
      if (visited.has(currentParentId)) {
        return; // Prevent infinite loops
      }
      visited.add(currentParentId);

      const children = this.getChildren(currentParentId, room);

      children.forEach((child) => {
        const childId = child.id;

        // Only add if not already added (prevents duplicates from multiple parent paths)
        if (childId && !addedDescendants.has(childId)) {
          addedDescendants.add(childId);

          if (includeDepth) {
            descendants.push({ ...child, depth });
          } else {
            descendants.push(child);
          }

          findDescendantsRecursive(childId, depth + 1);
        }
      });
    };

    findDescendantsRecursive(parentId);
    return descendants;
  }

  /**
   * Validate if a parent-child relationship would create a circular dependency
   * @param {string} parentId - Proposed parent ID
   * @param {string} childId - Proposed child ID
   * @param {Object} room - Room containing objects
   * @returns {Object} Validation result {valid: boolean, message: string}
   */
  static validateParentChild(parentId, childId, room) {
    if (!parentId || !childId || !room) {
      return { valid: false, message: 'Invalid parameters' };
    }

    if (parentId === childId) {
      return { valid: false, message: 'Object cannot be its own parent' };
    }

    // Check if parentId is already a descendant of childId
    const childDescendants = this.findAllDescendants(childId, room);
    const wouldCreateLoop = childDescendants.some(
      (desc) => desc.id === parentId
    );

    if (wouldCreateLoop) {
      return {
        valid: false,
        message: 'This relationship would create a circular dependency',
      };
    }

    // Check if the relationship type is valid
    const child = room.objects?.find((o) => o.id === childId);
    const parent = room.objects?.find((o) => o.id === parentId);

    if (child && parent) {
      const isValidType = this.validateRelationship(child, parent);
      if (!isValidType) {
        return {
          valid: false,
          message: `${child.type} cannot have ${parent.type} as parent`,
        };
      }
    }

    return { valid: true, message: 'Valid parent-child relationship' };
  }

  /**
   * Get the movement chain showing parent hierarchy
   * @param {string} objectId - ID of the object
   * @param {Object} room - Room containing objects
   * @returns {Array<string>} Array of object IDs from root to object
   */
  static getMovementChain(objectId, room) {
    const chain = this.getHierarchyChain(objectId, room);
    // Reverse to get root-to-child order
    return chain.reverse().map((item) => item.id);
  }

  /**
   * Apply offset to a position object
   * @param {Object} position - Position object with x, y, z or center fields
   * @param {Object} offset - Offset to apply {x, y, z}
   * @returns {Object} New position object with offset applied
   */
  static applyOffsetToPosition(position, offset) {
    if (!position || !offset) return position;

    const result = { ...position };

    if (typeof result.x === 'number') result.x += offset.x || 0;
    if (typeof result.y === 'number') result.y += offset.y || 0;
    if (typeof result.z === 'number') result.z += offset.z || 0;

    return result;
  }

  /**
   * Check if an object is a potential parent (has or can have children)
   * @param {Object} object - Object to check
   * @returns {boolean} True if object can be a parent
   */
  static isPotentialParent(object) {
    if (!object || !object.type) return false;

    const parentTypes = [
      'lower-cabinet',
      'upper-cabinet',
      'countertop',
      'wall',
      'shower',
      'bathtub',
    ];

    return parentTypes.includes(object.type) || isSink(object);
  }

  /**
   * Get object by ID from room
   * @param {string} objectId - ID of the object
   * @param {Object} room - Room containing objects
   * @returns {Object|null} Object or null if not found
   */
  static getObjectById(objectId, room) {
    if (!objectId || !room?.objects) return null;

    return room.objects.find((obj) => obj.id === objectId) || null;
  }

  /**
   * Get all fixture group members for a given object (excluding the object itself).
   * Objects sharing the same fixtureGroupId belong to the same fixture assembly
   * (e.g., showerhead, shower-controls, shower-niche all share a shower's group ID).
   * @param {string} objectId - ID of the object whose group members to find
   * @param {Object} room - Room containing objects
   * @returns {Array} Array of group member objects (excluding the object itself)
   */
  static getFixtureGroupMembers(objectId, room) {
    if (!objectId || !room?.objects) return [];

    const object = room.objects.find((o) => o.id === objectId);
    if (!object?.fixtureGroupId) return [];

    return room.objects.filter(
      (o) => o.fixtureGroupId === object.fixtureGroupId && o.id !== objectId
    );
  }

  /**
   * Reposition shower accessory objects after a shower is resized.
   * A part on one of the shower's walls — head, valve, niche, bench — stays
   * on THAT wall (`followShowerWall`, the rule the cloud's movers share); the
   * door stays at the front, as wide as the shower; anything else moves with
   * the shower's centre.
   *
   * @param {Object} shower - The resized shower object (with NEW center/dimensions)
   * @param {Object} oldCenter - Previous shower center {x, y, z}
   * @param {Object} oldDimensions - Previous shower dimensions {x, y, z}
   * @param {Object} room - Room containing objects (mutated in place)
   * @returns {Object} Updated room
   */
  static repositionShowerAccessories(shower, oldCenter, oldDimensions, room) {
    if (!shower?.fixtureGroupId || !room?.objects) return room;

    const groupMembers = this.getFixtureGroupMembers(shower.id, room);
    if (groupMembers.length === 0) return room;

    const rotY = shower.rotation?.y || 0;
    const sin = Math.sin(rotY);
    const cos = Math.cos(rotY);
    const newCenter = shower.center;
    const newDepth = shower.dimensions.z;
    const newWidth = shower.dimensions.x;
    const before = {
      center: oldCenter,
      dimensions: oldDimensions,
      rotation: shower.rotation,
    };

    for (const member of groupMembers) {
      const idx = room.objects.findIndex((o) => o.id === member.id);
      if (idx === -1) continue;

      const obj = room.objects[idx];
      const type = obj.type;

      if (SHOWER_WALL_PART_TYPES.has(type) && obj.center) {
        room.objects[idx] = {
          ...obj,
          center: followShowerWall(obj, before, shower),
        };
      } else if (type === 'straight-sliding-door-shower') {
        // Shower door: keep at front of shower, scale width to match
        const doorOffset = newDepth / 2;
        room.objects[idx] = {
          ...obj,
          center: {
            x: newCenter.x + doorOffset * sin,
            y: obj.center.y,
            z: newCenter.z + doorOffset * cos,
          },
          dimensions: {
            ...obj.dimensions,
            x: newWidth, // door width matches shower width
          },
        };
      } else {
        // Unknown group member: fallback to proportional repositioning
        const deltaX = newCenter.x - oldCenter.x;
        const deltaZ = newCenter.z - oldCenter.z;
        room.objects[idx] = {
          ...obj,
          center: {
            x: obj.center.x + deltaX,
            y: obj.center.y,
            z: obj.center.z + deltaZ,
          },
        };
      }
    }

    return room;
  }

  /**
   * Reposition bathtub accessory objects after a bathtub is resized.
   * Recalculates tub-support-box, tub-surface dimensions/positions from
   * bathtub geometry, and repositions faucets relative to the new tub center.
   *
   * @param {Object} bathtub - The resized bathtub object (with NEW center/dimensions)
   * @param {Object} oldCenter - Previous bathtub center {x, y, z}
   * @param {Object} oldDimensions - Previous bathtub dimensions {x, y, z}
   * @param {Object} room - Room containing objects (mutated in place)
   * @returns {Object} Updated room
   */
  static repositionBathtubAccessories(bathtub, oldCenter, oldDimensions, room) {
    if (!bathtub?.fixtureGroupId || !room?.objects) return room;

    const groupMembers = this.getFixtureGroupMembers(bathtub.id, room);
    if (groupMembers.length === 0) return room;

    // Constants from shared-lib/tub-surround/constants.js
    const DECK_THICKNESS = 0.05; // 2 inches
    const DECK_MARGIN = 0.1; // 4 inches total lip around tub

    const newCenter = bathtub.center;
    const tubWidth = bathtub.dimensions.x;
    const tubDepth = bathtub.dimensions.z;
    const tubHeight = bathtub.dimensions.y;

    const rotY = bathtub.rotation?.y || 0;

    // Surround footprint: keep whatever the existing support box has
    // (the bathroom-analyzer planned this allocation; we don't redecide it
    // every time a tub model swaps). Only grow if the new tub doesn't fit.
    const existingSupportBox = groupMembers.find(
      (m) => m.type === 'tub-support-box'
    );
    const minSurroundWidth = tubWidth + DECK_MARGIN;
    const minSurroundDepth = tubDepth + DECK_MARGIN;
    const surroundWidth = Math.max(
      existingSupportBox?.dimensions?.x || 0,
      minSurroundWidth
    );
    const surroundDepth = Math.max(
      existingSupportBox?.dimensions?.z || 0,
      minSurroundDepth
    );
    const surroundHeight = Math.max(0.45, tubHeight + DECK_THICKNESS);
    const supportHeight = surroundHeight - DECK_THICKNESS;

    // Derive floorY from existing support box, or from bathtub position
    let floorY = 0;
    if (existingSupportBox?.center && existingSupportBox?.dimensions) {
      floorY =
        existingSupportBox.center.y - existingSupportBox.dimensions.y / 2;
    } else {
      // Derive from bathtub: tubCenterY = deckCenterY - DECK_THICKNESS/2 - tubHeight/2
      // deckCenterY = floorY + supportHeight + DECK_THICKNESS/2
      // So: bathtub.center.y = floorY + supportHeight + DECK_THICKNESS/2 - DECK_THICKNESS/2 - tubHeight/2
      //     bathtub.center.y = floorY + supportHeight - tubHeight/2
      //     floorY = bathtub.center.y - supportHeight + tubHeight/2
      floorY = bathtub.center.y - supportHeight + tubHeight / 2;
    }

    const supportCenterY = floorY + supportHeight / 2;
    const deckCenterY = floorY + supportHeight + DECK_THICKNESS / 2;

    for (const member of groupMembers) {
      const idx = room.objects.findIndex((o) => o.id === member.id);
      if (idx === -1) continue;

      const obj = room.objects[idx];
      const type = obj.type;

      // Preserve cutout offset if it exists
      const cutoutOffsetX = obj.cutoutOffset?.x ? -obj.cutoutOffset.x : 0;
      const cutoutOffsetZ = obj.cutoutOffset?.z ? -obj.cutoutOffset.z : 0;

      if (type === 'tub-support-box') {
        // Local-frame dims + rotation matching the tub. Rendering applies the
        // rotation. Lazily migrates legacy data (rotation 0 + AABB dims) on
        // any tub edit.
        room.objects[idx] = {
          ...obj,
          center: {
            x: newCenter.x + cutoutOffsetX,
            y: supportCenterY,
            z: newCenter.z + cutoutOffsetZ,
          },
          dimensions: { x: surroundWidth, y: supportHeight, z: surroundDepth },
          rotation: { x: 0, y: rotY, z: 0 },
        };
      } else if (type === 'tub-surface') {
        room.objects[idx] = {
          ...obj,
          center: {
            x: newCenter.x + cutoutOffsetX,
            y: deckCenterY,
            z: newCenter.z + cutoutOffsetZ,
          },
          dimensions: { x: surroundWidth, y: DECK_THICKNESS, z: surroundDepth },
          rotation: { x: 0, y: rotY, z: 0 },
        };
      } else if (type === 'bathroom-tub-faucet') {
        // Keep faucet at same distance from tub edge (not from center).
        // Compute how far the faucet was from each old edge, then apply
        // the same edge distance from the new tub edges.
        const relX = obj.center.x - oldCenter.x;
        const relZ = obj.center.z - oldCenter.z;
        const oldHalfX = oldDimensions.x / 2;
        const oldHalfZ = oldDimensions.z / 2;
        const newHalfX = bathtub.dimensions.x / 2;
        const newHalfZ = bathtub.dimensions.z / 2;

        // Determine which edge the faucet is closest to and maintain
        // that edge distance. If faucet is beyond tub center in a direction,
        // shift by the half-dimension change so it stays on the rim.
        let newRelX = relX;
        let newRelZ = relZ;
        if (Math.abs(relX) > 0.01) {
          const edgeDist = (relX > 0 ? oldHalfX : -oldHalfX) - relX;
          newRelX = (relX > 0 ? newHalfX : -newHalfX) - edgeDist;
        }
        if (Math.abs(relZ) > 0.01) {
          const edgeDist = (relZ > 0 ? oldHalfZ : -oldHalfZ) - relZ;
          newRelZ = (relZ > 0 ? newHalfZ : -newHalfZ) - edgeDist;
        }

        room.objects[idx] = {
          ...obj,
          center: {
            x: newCenter.x + newRelX,
            y: obj.center.y, // keep original height
            z: newCenter.z + newRelZ,
          },
        };
      } else {
        // Unknown group member: fallback to delta translation
        const deltaX = newCenter.x - oldCenter.x;
        const deltaZ = newCenter.z - oldCenter.z;
        room.objects[idx] = {
          ...obj,
          center: {
            x: obj.center.x + deltaX,
            y: obj.center.y,
            z: obj.center.z + deltaZ,
          },
        };
      }
    }

    return room;
  }

  /**
   * Convert legacy parent fields to new parents array
   * Used during migration/transition period
   * @param {Object} object - Object to migrate
   * @returns {Object} Object with parents array
   */
  static migrateLegacyParentFields(object) {
    if (!object) return object;

    // If already has parents array, skip migration
    if (object.parents && Array.isArray(object.parents)) {
      return object;
    }

    // Initialize parents array
    object.parents = [];

    // Migrate wall-mounted parent fields
    const wallParentId =
      object.parentIdentifier ||
      object.parentWallId ||
      object.parentWallIds?.[0] ||
      object.parentWall;

    if (wallParentId) {
      object.parents.push({ id: wallParentId, type: 'wall' });

      // Clean up old fields
      delete object.parentIdentifier;
      delete object.parentWallId;
      delete object.parentWallIds;
      delete object.parentWall;
    }

    // Migrate countertop parent (for faucets)
    if (object.countertopId) {
      object.parents.push({ id: object.countertopId, type: 'countertop' });
      delete object.countertopId;
    }

    // Migrate sink parent (for faucets)
    if (object.sinkId) {
      // We don't know the exact sink type, so use generic 'sink'
      // This should be updated when we have more context
      object.parents.push({ id: object.sinkId, type: 'sink' });
      delete object.sinkId;
    }

    // Migrate cabinet parent (for countertops)
    if (object.parentObjectId) {
      // Assume it's a cabinet if it has parentObjectId
      object.parents.push({ id: object.parentObjectId, type: 'lower-cabinet' });
      delete object.parentObjectId;
    }

    // `cabinetWallRefs` is the NEW (post-2026-05-12) field for cabinet
    // line membership — refs into room.cabinetLayout.cabinetWalls. It
    // serves a different purpose than `parents` (geometric alignment
    // vs. ownership hierarchy) and is intentionally NOT migrated into
    // `parents`. See claude-docs/2026-05-12-cabinet-layout-integration.md.
    // No action here; left as a defensive marker so future maintainers
    // know not to fold it into `parents`.

    return object;
  }

  /**
   * Cascade resize to all primary children when a parent is resized.
   * Scales child dimensions proportionally and repositions relative to new parent center.
   * @param {string} parentId - ID of the resized parent
   * @param {Object} oldDimensions - Previous dimensions {x, y, z}
   * @param {Object} newDimensions - New dimensions {x, y, z}
   * @param {Object} oldCenter - Previous center {x, y, z}
   * @param {Object} newCenter - New center {x, y, z}
   * @param {Object} room - Room containing objects
   * @param {Object} options - Configuration options
   * @param {boolean} options.recursive - Cascade to descendants (default: true)
   * @param {number} options.maxDepth - Max recursion depth (default: 10)
   * @returns {Object} Updated room with cascaded resize changes (new object, not mutated)
   */
  static cascadeResize(
    parentId,
    oldDimensions,
    newDimensions,
    oldCenter,
    newCenter,
    room,
    options = {}
  ) {
    if (!parentId || !oldDimensions || !newDimensions || !room?.objects) {
      return room;
    }

    const config = {
      recursive: true,
      maxDepth: 10,
      ...options,
    };

    // Compute scale factors (avoid division by zero)
    const scaleX = oldDimensions.x > 0 ? newDimensions.x / oldDimensions.x : 1;
    const scaleY = oldDimensions.y > 0 ? newDimensions.y / oldDimensions.y : 1;
    const scaleZ = oldDimensions.z > 0 ? newDimensions.z / oldDimensions.z : 1;

    // Clone room for immutable update
    const updatedRoom = JSON.parse(JSON.stringify(room));

    const visited = new Set();

    const resizeChildren = (
      curParentId,
      curOldCenter,
      curNewCenter,
      curScaleX,
      curScaleY,
      curScaleZ,
      depth = 0
    ) => {
      if (depth >= config.maxDepth || visited.has(curParentId)) {
        return;
      }
      visited.add(curParentId);

      const children = this.getChildren(curParentId, updatedRoom, null, true);

      children.forEach((child) => {
        if (!child.center || !child.dimensions) return;

        // Reposition: compute offset from old parent center, scale it, add to new center
        const offsetX = child.center.x - curOldCenter.x;
        const offsetY = child.center.y - curOldCenter.y;
        const offsetZ = child.center.z - curOldCenter.z;

        child.center.x = curNewCenter.x + offsetX * curScaleX;
        child.center.y = curNewCenter.y + offsetY * curScaleY;
        child.center.z = curNewCenter.z + offsetZ * curScaleZ;

        // Scale dimensions proportionally
        const oldChildDims = {
          x: child.dimensions.x,
          y: child.dimensions.y,
          z: child.dimensions.z,
        };
        child.dimensions.x *= curScaleX;
        child.dimensions.y *= curScaleY;
        child.dimensions.z *= curScaleZ;

        // Recurse to grandchildren
        if (config.recursive && child.id) {
          resizeChildren(
            child.id,
            {
              x: curOldCenter.x + offsetX,
              y: curOldCenter.y + offsetY,
              z: curOldCenter.z + offsetZ,
            },
            child.center,
            curScaleX,
            curScaleY,
            curScaleZ,
            depth + 1
          );
        }
      });
    };

    resizeChildren(parentId, oldCenter, newCenter, scaleX, scaleY, scaleZ);

    return updatedRoom;
  }
}

export default ParentChildSystem;
